import Vue from "vue";
import _ from "lodash";

// Macht eine beliebig tief verschachtelte Feldliste flach, verwirft
// Einträge ohne key und entfernt doppelte keys (erstes Vorkommen bleibt).
// Repariert so auch fehlerhaft gespeicherte Layouts (z.B. 3D statt 2D).
export function flattenFields(array) {
  if (!Array.isArray(array)) return [];

  const seen = new Set();
  return _.flattenDeep(array).filter((f) => {
    if (!f || typeof f !== "object" || !f.key || seen.has(f.key)) {
      return false;
    }
    seen.add(f.key);
    return true;
  });
}

// Takes simple array and males 2d Array
function fields2dEncoder(array) {
  let newArray = [];
  const fields = flattenFields(array);
  if (fields.length === 0) return newArray;

  // Anzahl der Items pro Reihe
  const perRow = fields.length > 4 ? 3 : 2;

  fields.forEach((e, idx) => {
    if (idx % perRow === 0) {
      newArray.push([]);
    }
    newArray[newArray.length - 1].push(e);
  });

  return newArray;
}

// Takes 2d Array and makes simple array
function fields2dDecoder(array) {
  return flattenFields(array);
}

// Felder der Vorderseite, die per Schalter (front_field_state) wählbar sind
export const FRONT_FIELD_KEYS = [
  "date_of_birth",
  "role",
  "valid_until",
  "issuing_authority_name",
];

// Felder der Vorderseite passend zu den Schaltern (front_field_state):
// ausgeschaltete Felder werden entfernt, eingeschaltete aber fehlende Felder
// ergänzt (z.B. weil die Liste durch einen früheren Fehler leer gespeichert
// wurde). Felder ohne Schalter bleiben erhalten.
export function activeFrontFields(stylesheet) {
  const state = stylesheet.front_field_state || {};
  const fields = flattenFields(stylesheet.fields_front).filter(
    (f) => state[f.key] !== false
  );

  FRONT_FIELD_KEYS.forEach((key) => {
    if (state[key] === true && !fields.some((f) => f.key === key)) {
      fields.push({ key });
    }
  });

  return fields;
}

// Wandelt die 2D-Feldlisten aus der API in flache Listen fürs Frontend um
function decodeLayout(layout) {
  layout.stylesheet.fields_front = activeFrontFields(layout.stylesheet);
  layout.stylesheet.fields_back = fields2dDecoder(
    layout.stylesheet.fields_back
  );
  return layout;
}

// Kopie des Layouts mit 2D-Feldlisten für die API, ohne das übergebene
// Objekt zu verändern (sonst würde ein erneutes Speichern doppelt kodieren)
function encodeLayout(layout) {
  return {
    ...layout,
    stylesheet: {
      ...layout.stylesheet,
      fields_front: fields2dEncoder(activeFrontFields(layout.stylesheet)),
      fields_back: fields2dEncoder(layout.stylesheet.fields_back),
    },
  };
}

export default {
  namespaced: true,
  state: {
    layout: {},
    layouts: {},
    templates: {},
    customerTemplates: {},
  },

  getters: {
    getLayout(state) {
      return state.layout;
    },
    getLayouts(state) {
      return state.layouts;
    },
    getTemplates(state) {
      return state.templates;
    },
    getCustomerTemplates(state) {
      return state.customerTemplates;
    },
  },

  mutations: {
    setLayout(state, layout) {
      state.layout = layout;
    },
    setLayouts(state, layouts) {
      state.layouts = layouts;
    },
    setTemplates(state, templates) {
      state.templates = templates;
    },
    setCustomerTemplates(state, customerTemplates) {
      state.customerTemplates = customerTemplates;
    },
    setLayoutImageTopRightLogo(state, id) {
      state.layout.header_picture_id = id;
    },
  },

  actions: {
    async loadLayouts(ctx) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });

      const url = "/api/v1/customizings/layouts";
      return await Vue.prototype
        .axios({
          method: "get",
          url: url,
          params: {
            sort_by: "name",
          },
          headers: headers,
        })
        .then((resp) => {
          ctx.commit("setLayouts", resp.data);
          return resp.status;
        })
        .catch((error) => {
          // // console.log(error);
          return error.response.status;
        });
    },

    async loadTemplates(ctx) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });

      const url = "/api/v1/customizings/layouts";
      return await Vue.prototype
        .axios({
          method: "get",
          url: url,
          params: {
            is_template: true,
            sort_by: "name",
          },
          headers: headers,
        })
        .then((resp) => {
          ctx.commit("setTemplates", resp.data);
          return resp.status;
        })
        .catch((error) => {
          // // console.log(error);
          return error.response.status;
        });
    },

    async loadCustomerTemplates(ctx) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });
      const user = await ctx.rootGetters["auth/getUser"];

      const url = "/api/v1/customizings/layouts";
      return await Vue.prototype
        .axios({
          method: "get",
          url: url,
          params: {
            is_template: true,
            template_of_customer_id: user.customer_id,
          },
          headers: headers,
        })
        .then((resp) => {
          ctx.commit("setCustomerTemplates", resp.data);
          return resp.status;
        })
        .catch((error) => {
          // // console.log(error);
          return error.response.status;
        });
    },

    async loadLayout(ctx, layoutId) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });
      const user = ctx.rootGetters["auth/getUser"];

      const url = "/api/v1/customizings/layouts/" + layoutId;
      return await Vue.prototype
        .axios({
          method: "get",
          url: url,
          headers: headers,
        })
        .then((resp) => {
          ctx.commit("setLayout", decodeLayout(resp.data));
          return resp.status;
        })
        .catch((error) => {
          // // console.log(error);
          return error.response.status;
        });
    },

    async createLayout(ctx, layout) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });
      const user = ctx.rootGetters["auth/getUser"];

      const url = "/api/v1/customizings/layouts";
      return await Vue.prototype
        .axios({
          method: "post",
          url: url,
          data: encodeLayout(layout),
          headers: headers,
        })
        .then((resp) => {
          ctx.commit("setLayout", decodeLayout(resp.data));
          ctx.dispatch("loadCustomerTemplates");
          return resp.status;
        })
        .catch((error) => {
          // // console.log(error);
          return error.response.status;
        });
    },

    async updateLayout(ctx, layout) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });
      const user = ctx.rootGetters["auth/getUser"];

      const url = "/api/v1/customizings/layouts/" + layout.id;
      return await Vue.prototype
        .axios({
          method: "put",
          url: url,
          data: encodeLayout(layout),
          headers: headers,
        })
        .then((resp) => {
          ctx.dispatch("loadCustomerTemplates");
          return resp.status;
        })
        .catch((error) => {
          // // console.log(error);
          return error.response.status;
        });
    },

    async deleteLayout(ctx, id) {
      const headers = await ctx.dispatch("auth/getHeaders", null, {
        root: true,
      });
      const user = ctx.rootGetters["auth/getUser"];

      const url = "/api/v1/customizings/layouts/" + id;
      return await Vue.prototype
        .axios({
          method: "delete",
          url: url,
          headers: headers,
        })
        .then((resp) => {
          ctx.dispatch("loadCustomerTemplates");
          return resp.status;
        })
        .catch((error) => {
          // // console.log(error);
          return error.response.status;
        });
    },

    setLayoutImageTopRightLogo(ctx, imageId) {
      ctx.commit("setLayoutImageTopRightLogo", imageId);
    },
  },
};
